<script lang="ts">
    import { _, isLoading, locale } from 'svelte-i18n';
    import * as Sentry from '@sentry/browser';

    import { afterUpdate, onMount } from 'svelte';
    import { Router, Route, navigate } from 'svelte-routing';
    import { fade } from 'svelte/transition';
    import Spinner from './lib/shared/Spinner/Spinner.svelte';
    import {
        debug,
        lastClickedPosition,
        leftPageWidthPx,
        mainMenu,
        planning,
        readonlyMode,
    } from './stores/store';
    import srcLogoAutoMapaLastMile from './lib/shared/Icons/svg/AM_lastmile.svg';
    import TargeoMap from './lib/shared/TargeoMap.svelte';
    import MainMenuVertical from './lib/shared/Menu/MainMenuVertical.svelte';
    import './locale/loadLocalesAsync';
    import FilterManager from './lib/components/Filter/FilterManager.svelte';
    import {
        api,
        assignment_job_filter_config,
        clientSettings,
        jobs_filter_config,
        customerSettings,
        tactical_plan_rules_filter_config,
        mla,
    } from './stores/api';
    import { isMapLoaded } from './stores/store';
    import Dropdown2 from './lib/components/MenuDropdown/Dropdown2.svelte';
    import SimpleStatus from './lib/shared/SimpleStatus.svelte';
    import { accountName } from './stores/store';
    import { Modals, closeModal, openModal } from 'svelte-modals';
    import { setCookie } from './lib/utilities/cookie';
    import { Umami } from './lib/utilities/Umami';
    import { SettingsApi } from './lib/components/Settings/SettingsApi';
    import TelematicsFilter from './lib/components/telematics/TelematicsFilter.svelte';
    import AppMainStatus from './lib/components/AppMainStatus/AppMainStatus.svelte';
    import { FilterManagerIO } from './lib/components/telematics/components/FilterByParameters/FilterManagerIO';
    import { get } from 'svelte/store';
    import {
        ClientSettingsSchema,
        defaultClientSettings,
    } from './lib/components/Settings/settings';
    import ModalInfo from './lib/shared/Modals/ModalInfo.svelte';
    import MenuContextParent from './lib/components/MenuDropdown/MenuContext/MenuContextParent.svelte';
    import { MonitorSessionStatus } from './lib/components/Status/MonitorSessionStatus';
    import ModalError from './lib/shared/Modals/ModalError.svelte';
    import { app } from './stores/app';
    import { Account } from './lib/components/Accounts/Account';
    import { MapLayerApp } from './lib/utilities/MapLayer/MapLayerApp';
    import { MapLayerApi } from './lib/utilities/MapLayer/MapLayerApi';
    import MapLegend from './lib/shared/MapLegend/MapLegend.svelte';
    import { MapLegendLayers, MapLegendStoreX } from './lib/shared/MapLegend/MapLegendStore';
    import { MapLegendStoreZoom } from './lib/shared/MapLegend/MapLegendStore.js';
    import { StatusApi } from './lib/components/Status/StatusApi';
    import { Navigation } from './lib/utilities/Navigation';

    export let url = '';
    export let initialSettings = {};

    let loadingSettings: boolean = true;
    let error: string = '';
    let appIsReady: boolean = false;
    let appLoading: boolean = true;
    let leftPageWidth: number = 600;
    let draggableBorderWidth: number = 10;
    let draggableBorder: HTMLElement;
    let appLocale: string = '';

    function resizeMap() {
        if (
            typeof Targeo !== 'undefined' &&
            typeof Targeo.Base !== 'undefined' &&
            typeof Targeo.Base.Map !== 'undefined'
        ) {
            try {
                Targeo.Base.Map.updateSize();
            } catch (e) {
                console.error('Error resizing map', e);
            }
        }
    }

    function navigateToLoginPage(e: string) {
        let lang = localStorage.getItem('locale');
        window.location.href =
            '//' +
            window.location.hostname +
            '/login/login.html?e=' +
            e +
            (lang ? '&ln=' + lang : '');
    }

    afterUpdate(() => {
        // console.log('App.svelte, afterUpdate');
        resizeMap();
    });

    const onLogout = () => {
        console.info('logout_auto');
        Umami.event('logout_auto');
        if (typeof $api.setIsLoggedOut === 'function') $api.setIsLoggedOut(true);
        openModal(ModalInfo, {
            text: $_('logout_info_and_what_next'),
            onClick: () => {
                if (typeof initialSettings.onLogout === 'function') {
                    console.log('running initialSettings.onLogout()');
                    initialSettings.onLogout();
                } else {
                    console.log('onLogout is not a function');
                }
            },
        });
    };

    $api.setToken(initialSettings?.token || '');
    $api.onLogout(onLogout);
    new MonitorSessionStatus(onLogout);

    async function isLoadingPromise(): Promise<void> {
        return new Promise((resolve, reject) => {
            const interval = setInterval(() => {
                if (!$isLoading) {
                    clearInterval(interval);
                    resolve();
                }
            }, 250);
        });
    }

    async function isjQueryLoaded(): Promise<void> {
        return new Promise((resolve, reject) => {
            const interval = setInterval(() => {
                if (window.jQuery) {
                    clearInterval(interval);
                    resolve();
                }
            }, 250);
        });
    }

    document.addEventListener('click', event => {
        $lastClickedPosition.x = event.clientX;
        $lastClickedPosition.y = event.clientY;
        // console.log(
        //     `Last clicked position, click: X = ${$lastClickedPosition.x}, Y = ${$lastClickedPosition.y}`
        // );
    });

    onMount(() => {
        console.info('App onMount');
        $api.setApiUrl(initialSettings?.apiUrl);
        Promise.all([
            isLoadingPromise(),
            isjQueryLoaded(),
            SettingsApi.getCustomerSettings(get(api)),
            SettingsApi.getClientSettings(get(api), 'settings', ClientSettingsSchema),
            SettingsApi.getFilterConfig($api, 'job'),
            SettingsApi.getFilterConfig($api, 'assignment_job'),
            SettingsApi.getFilterConfig($api, 'tactical_plan_rule'),
            SettingsApi.isAdmin($api),
            SettingsApi.getDemoAccounts($api, true),
            StatusApi.check($api),
        ])
            .then(response => {
                console.info('SettingsApi.getClientSettings, response', response[3]);
                console.info('App, onMount, then');
                $customerSettings = response[2].settings;
                $accountName = response[2].webuser_name;
                try {
                    $clientSettings = ClientSettingsSchema.parse(response[3]);
                } catch (e) {
                    console.error(
                        'Error parsing loaded settings, using default settings.',
                        response[3],
                        e
                    );
                    openModal(ModalInfo, {
                        text: $_('Error parsing loaded settings, using default settings.'),
                    });
                    $clientSettings = ClientSettingsSchema.parse(defaultClientSettings);
                }
                $readonlyMode = $clientSettings.display.readonlyMode;
                if ($clientSettings.display.selectedDay) {
                    $planning.selectedDay = $clientSettings.display.selectedDay;
                }
                if ($readonlyMode) {
                    console.log('Setting readonlyMode = true, redirecting to planning');
                    Navigation.navigate('/planning');
                }

                $jobs_filter_config = response[4] || null;
                $assignment_job_filter_config = response[5] || null;
                $tactical_plan_rules_filter_config = response[6] || null;

                $app.isAdmin = response[7] || false;
                $app.account = new Account().set(response[8][0], null) || null;

                $app.commit_hash = response[9]['commit_hash'] || null;

                Sentry.setContext('app', {
                    isAdmin: $app.isAdmin,
                    customer_id: get(app).account?.getCustomerId() ?? null,
                    name: get(app).account?.getName() ?? null,
                    webuser_id: get(app).account?.getWebuserId() ?? null,
                    commit_hash: $app.commit_hash,
                });

                if ($app.account && $app.account?.isExpired() === true) {
                    Sentry.captureMessage(`Account expired, customer_id: ${$app.account.getCustomerId()}`, {
                        level: 'warning',
                    });
                    openModal(ModalError, {
                        header: $_('account_expired'),
                        message:
                            $_('this_account_is_expired') +
                            ` (#cid:
                            ${$app.account?.getCustomerId()})`,
                        onClick: () => {
                            // if slave customer is selected, do not redirect - allow to change slave customer in list
                            if ($app.account?.getMasterCustomerId() !== null && $app.account?.getCustomerId() !== $app.account?.getMasterCustomerId()) {
                                console.log('Account is expired, but slave customer is selected, not redirecting to login page, allowing to change slave customer in list', $app.account?.getMasterCustomerId(), $app.account?.getCustomerId());
                                return;
                            } else {
                                navigateToLoginPage('request_permission_denied');
                            }
                        },
                    });
                }

                Umami.init().then(()=> {
                    Umami.event('load_app', {
                        isAdmin: $app.isAdmin,
                        customer_id: get(app).account?.getCustomerId() ?? null,
                        name: get(app).account?.getName() ?? null,
                        webuser_id: get(app).account?.getWebuserId() ?? null,
                    });
                });

                let tempLocale = $clientSettings.locale || $locale;
                console.log('Locale1:', tempLocale);
                $locale = $app.account ? $app.account.getDefaultLocale(tempLocale) : tempLocale;
                if ($locale) {
                    appLocale = $locale;
                    // Set cookie for backend i18n (with domain to share across subdomains)
                    setCookie('ln', $locale);
                }
                console.log('Locale2:', $locale);

                appIsReady = true;
                if (initialSettings?.token === '') appIsReady = false;
                if ($customerSettings?.map_key === '') appIsReady = false;

                $MapLegendStoreX = parseInt(localStorage.getItem('leftPageWidth') || '600') + 30;
                leftPageWidthPx.set(parseInt(localStorage.getItem('leftPageWidth') || '600'));

                // wait for draggableBorder is not undefined
                Promise.all([
                    new Promise((resolve, reject) => {
                        const interval = setInterval(() => {
                            if (draggableBorder) {
                                clearInterval(interval);
                                resolve('');
                            }
                        }, 250);
                    }),
                ]).then(_ => {
                    if (draggableBorder) {
                        let leftPage = document.querySelector('.visible') as HTMLElement;
                        let minimumPageWidth = 400;
                        let maximumPageWidth = 900;
                        leftPageWidth = Math.max(
                            parseInt(localStorage.getItem('leftPageWidth') || '600'),
                            240
                        );
                        draggableBorder.style.left =
                            leftPageWidth - draggableBorderWidth - 2 + 'px';
                        draggableBorder.style.width = draggableBorderWidth + 'px';
                        leftPage.style.width = leftPageWidth - 2 + 'px';
                        // @ts-ignore jQuery
                        (<any>jQuery(draggableBorder)).draggable({
                            axis: 'x',
                            start: (e: Event) => {},
                            drag: (e: Event, ui: any) => {
                                let moveX = ui.position.left + draggableBorderWidth; // + 2;
                                if (leftPage.parentElement) {
                                    maximumPageWidth = leftPage.parentElement.clientWidth - 100;
                                }
                                if (moveX > maximumPageWidth) {
                                    moveX = maximumPageWidth;
                                    ui.position.left = moveX - draggableBorderWidth; // -2;
                                }
                                if (moveX < minimumPageWidth) {
                                    moveX = minimumPageWidth;
                                    ui.position.left = moveX - draggableBorderWidth; // -2;
                                }
                                leftPage.style.width = moveX + 'px';
                                setTimeout(() => {
                                    window.dispatchEvent(new Event('resize'));
                                    $MapLegendStoreX = moveX + 30;
                                    leftPageWidthPx.set(moveX);
                                }, 0);
                            },
                            stop: (e: Event, ui: any) => {
                                localStorage.setItem('leftPageWidth', ui.position.left.toString());
                                window.dispatchEvent(new Event('resize'));
                                $MapLegendStoreX = ui.position.left + 30;
                                leftPageWidthPx.set(ui.position.left);
                            },
                        });
                        // draggable(draggableBorder, draggableBorderWidth, leftPageWidth, (pageWidth) => {
                        //     setTimeout( () => {
                        //         localStorage.setItem('leftPageWidth', pageWidth.toString());
                        //     }, 0);
                        // });
                    }
                });
            })
            .catch(e => {
                console.error('onMount error', e);
                error = e;
                if (e == 'request_authentication_failed' || e == 'request_permission_denied') {
                    console.debug('redirecting to login page...', e);
                    navigateToLoginPage(e);
                }
                if (e == 'demo_period_ended') {
                    console.debug('redirecting to login page...', e);
                    alert($_(e));
                    navigateToLoginPage(e);
                }
                alert($_('app_init_error'));
            })
            .finally(() => {
                appLoading = false;
            });
    });

    $: {
        if (
            initialSettings?.map_key === '' ||
            initialSettings?.map_key !== $customerSettings?.map_key
        ) {
            initialSettings = {
                ...initialSettings,
                map_key: $customerSettings?.map_key || '',
            };
        }
    }

    $: {
        if ($locale && appLocale != $locale) {
            appLocale = $locale;
        }
    }

    function showTemplateWindow() {
        openModal(ModalInfo, {
            text: $_('template_account_info'),
        });
    }
</script>

{#if $customerSettings.is_template}
    <div
        style="width:100%;height:26px;background:rgb(255, 120, 0);text-align: center;font-size: 14px;line-height: 26px;"
    >
        {$_('template_account_banner')} <a href="#" on:click={showTemplateWindow} style="color:#000"
            >{$_('read_more')}</a
        >
    </div>
{/if}
<div id="page" class="main">
    {#if !appIsReady}
        {#if appLoading && false}{:else if $debug}
            <div
                style="width:100vw; height:100vh; display: flex; justify-content: center; align-items: center; "
            >
                <div
                    style="display: grid; width: 450px;  grid-template-columns: 50% 50%; max-height: 400px;
                    overflow: auto; grid-template-rows: min-content;  gap: 5px 10px;  grid-template-areas: '. .'; "
                >
                    <SimpleStatus
                        text="Translations"
                        check={!$isLoading}
                        success="OK"
                        failure="Loading"
                    />
                    <SimpleStatus
                        text="API Token"
                        check={initialSettings?.token !== ''}
                        success="OK"
                        failure="Not found"
                    />
                    <SimpleStatus
                        text="API Settings"
                        check={!loadingSettings && error === ''}
                        success="OK"
                        failure={(error && error) || 'Checking...'}
                    />
                    <SimpleStatus
                        text="Map Token"
                        check={initialSettings?.map_key !== ''}
                        success="OK"
                        failure="Not found"
                    />
                </div>
            </div>
        {:else}
            <div
                style="width:100vw; height:100vh; display: flex; justify-content: center; align-items: center; "
            >
                <Spinner />
            </div>
        {/if}
    {:else}
        <Router {url}>
            <MenuContextParent />
            <Modals>
                <div slot="backdrop" class="backdrop" on:click={closeModal} />
            </Modals>

            <div id="page_header_logo">
                <img src={srcLogoAutoMapaLastMile} alt="AutoMapa LastMile" />
            </div>

            <div class="main_filter_background">
                <div class="page_filters">
                    <AppMainStatus />
                </div>
            </div>

            <div class="page_header_buttons">
                <Dropdown2 icon="b" onLogout={initialSettings?.onLogout} />
            </div>

            <div id="page_header_menu">
                <!--    <PlanningModeSwitch />-->
            </div>

            <div class="filter_details"></div>

            <div class="page_content_with_menu" class:menu-vertical={true}>
                <div class="main_menu">
                    <MainMenuVertical />
                </div>

                <div
                    class="page_content"
                    class:left-side={$mainMenu.items.find(i => i.id === $mainMenu.currentPage)
                        ?.side === 'left'}
                    class:both-sides={$mainMenu.items.find(i => i.id === $mainMenu.currentPage)
                        ?.side === 'both'}
                >
                    <div class="visible" class:fullscreen-mode={$mainMenu.fullscreenMode}>
                        {#each $mainMenu.items as item, index (item.id)}
                            <Route path={item.to} component={item.component} />
                        {/each}
                    </div>
                    <div
                        class="draggable-border"
                        class:fullscreen-mode={$mainMenu.fullscreenMode}
                        bind:this={draggableBorder}
                    ></div>
                    {#if initialSettings?.map_key}
                        <TargeoMap
                            onMapLoad={map => {
                                console.log('onMapLoad, init');
                                $isMapLoaded = true;
                                let timestamp = new Date().getTime().toString();
                                let token = $api.getToken();
                                let customerId = $app.account.getCustomerId().toString();
                                let tiles_vrp = {
                                    id: 'vrp',
                                    label: 'Zlecenia historyczne',
                                    tile_url:
                                        $api.getApiUrl() +
                                        '/tiles/tile?x={x}&y={y}&z={z}&t=' +
                                        timestamp +
                                        '&token=' +
                                        token +
                                        '&cid=' +
                                        customerId,
                                    enabled: true,
                                    available: true,
                                    visible: true,
                                };
                                $mla.addLayer(tiles_vrp)
                                    .includeToMap(map)
                                    .then(key_layers_included => {
                                        console.log(
                                            'Included to map: ' + key_layers_included.join(', ')
                                        );
                                        map.listen(
                                            'onZoomChange',
                                            () => {
                                                console.log('onZoomChange', map.getZoom());
                                                MapLegendStoreZoom.set(map.getZoom());
                                            },
                                            false
                                        );
                                        MapLegendLayers.set($mla.getAvailable());
                                    })
                                    .finally(() => {
                                        console.log('onMapLoad, finally');
                                        // $onMapLoaded = true;
                                    });
                            }}
                            map_key={initialSettings?.map_key}
                            uu={initialSettings?.token || ''}
                            ln={appLocale || 'pl'}
                            pointClusterDistance={$clientSettings.display.point_cluster_distance}
                        />
                        <MapLegend />
                    {/if}
                </div>
            </div>
        </Router>
    {/if}
</div>

<style>
    :root {
        --top-header-height: 69px;
        --bottom-header-height: 3px;
    }

    #page.main {
        display: grid;
        grid-template-rows: var(--top-header-height) var(--bottom-header-height) 1fr;
        grid-template-columns: 250px 1fr 76px;
        grid-template-areas:
            'page_header_logo main_filter_background page_header_buttons'
            'page_header_menu filter_details filter_details'
            'page_content_with_menu page_content_with_menu page_content_with_menu';
        height: 100vh;
        background-color: white;
    }

    .main_menu.planning-mode {
        /*background-color: #84c32855;*/
        /*width: 160px;*/
        /*z-index: 300;*/
    }

    .page_content_with_menu {
        grid-area: page_content_with_menu;
        border-bottom: 1px solid black;
        overflow: hidden; /* important to keep map in window */
    }

    .page_content {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        width: 100%;
    }

    .menu-vertical {
        display: flex;
        flex-direction: row;
    }

    .page_content_component {
        display: flex;
        flex-direction: row;
        justify-content: center;
        height: 100%;
    }

    .visible {
        /*flex: 1 0 700px;*/
        flex: 1 0 auto;
        /*border-right: 2px solid var(--automapa-blue-light);*/
        /*width: 600px;*/
        /*min-width: 300px;*/
        user-select: none;
        /*min-width: 699px;*/
    }
    .visible.fullscreen-mode {
        width: 100% !important;
    }

    .not-visible {
        display: none;
    }

    .page_content.left-side {
        flex-direction: row;
    }
    .page_content.both-sides {
        flex-direction: row;
    }
    .page_content.both-sides .visible {
        flex: 0 0 100%;
    }

    #page_header_logo {
        grid-area: page_header_logo;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #1c5dc7;
        padding: 16px;
    }

    #page_header_logo img {
        width: 80%;
        height: auto;
    }

    #page_header_menu {
        flex-direction: row;
        font-size: 12px;
        height: 100%;
        padding-bottom: 2px;
        box-sizing: border-box;
        background-color: #6c6b6a;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        padding-left: 10%;
    }

    .filter_details {
        grid-area: filter_details;
        user-select: none;
        background-color: #6c6b6a;
        color: white;
        display: flex;
        flex-wrap: nowrap;
        white-space: nowrap;
        align-items: center;
        overflow: hidden;
        /*padding: 5px;*/
        padding-left: 15px;
        column-gap: 2px;
    }

    .page_header_buttons {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-content: center;
        align-items: center;
        background-color: #1c5dc7;
        z-index: 2000;
        /*padding-right: 20px;*/
    }

    .page_content {
        position: relative;
        border-top: 0px;
        /*z-index: 31;*/
    }

    .page_filters {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        padding-left: 2px;
        background-color: var(--analytics-status-background);
        height: 100%;
        border-top-left-radius: 3px;
        border-top-right-radius: 3px;
        user-select: none;
        width: 100%;
        overflow: hidden;
    }

    .main_filter_background {
        grid-area: main_filter_background;
        display: flex;
        align-items: center;
        background-color: #1c5dc7;
        padding: 5px 0 0 0;
        flex: 10 1 100%;
    }

    .draggable-border {
        background-color: transparent;
        /*width: 50px;*/
        cursor: col-resize;
        position: absolute;
        height: 100%;
        border-right: 2px solid var(--automapa-blue-light);
        z-index: 1;
    }
    .draggable-border:hover {
        background-color: var(--main-selected-background);
    }
    .draggable-border.fullscreen-mode {
        display: none;
    }

    .backdrop {
        position: fixed;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 1001;
    }
</style>
